/**
 * RoadOption — single suggestion row inside the road Combobox dropdown.
 *
 * Renders the localized full display name plus a colored road-type badge.
 * The badge color/label mapping mirrors the previous `renderRoadOption` logic.
 */

import { memo, useMemo } from 'react';
import { Group, Text, Badge } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { getRoadType } from '@/utils/roadTypeHelpers';
import { formatRoadDisplayName } from '@/utils/roads';
import { ROAD_DISPLAY_FIELD_CONSTANTS } from '../RoadDisplayField.constants';
import type { RoadOptionProps } from '../RoadDisplayField.types';
import classes from '../RoadDisplayField.module.css';

// Memoized: the parent dropdown re-renders on every keystroke / `isLoading` /
// `hasNextPage` change, but each row's `suggestion` is a stable object, so the
// `formatRoadDisplayName` + `getRoadType` work below need only run when the
// row's `ref`/`roadType` actually change.
function RoadOptionComponent({ suggestion }: RoadOptionProps) {
  const { t } = useTranslation();

  const displayName = useMemo(
    () => formatRoadDisplayName(suggestion.ref, suggestion.roadType, t),
    [suggestion.ref, suggestion.roadType, t]
  );
  const roadTypeInfo = useMemo(
    () => getRoadType(suggestion.roadType, t),
    [suggestion.roadType, t]
  );

  return (
    <Group justify="space-between" wrap="nowrap" className={classes.option}>
      <Text size="sm" truncate>
        {displayName}
      </Text>
      <Badge
        variant="light"
        color={roadTypeInfo.color}
        size={ROAD_DISPLAY_FIELD_CONSTANTS.BADGE.SIZE}
      >
        {roadTypeInfo.label}
      </Badge>
    </Group>
  );
}

export const RoadOption = memo(RoadOptionComponent);
